// Runs and conversations on the cosmos API (p1f): reads, start, cancel and erase. A run's events
// are followed with useCosmosStream (api/event-stream.ts).
import {
  ConversationView,
  CreatedConversation,
  FINAL,
  RunView,
  StartedRun,
} from "@cosmosai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useCosmos } from "./cosmos";
import { read } from "./read";

export interface RunFilters {
  agent?: string;
  conversation?: string;
}

/** Keys under ["cosmos", workspace]; runs and conversations of a project share a prefix each. */
export const runKeys = {
  runsOf: (workspace: string, project: string) => ["cosmos", workspace, "runs", project] as const,
  runs: (workspace: string, project: string, filters: RunFilters = {}) =>
    ["cosmos", workspace, "runs", project, filters] as const,
  runOf: (workspace: string, project: string) => ["cosmos", workspace, "run", project] as const,
  run: (workspace: string, project: string, id: string) =>
    ["cosmos", workspace, "run", project, id] as const,
  conversationsOf: (workspace: string, project: string) =>
    ["cosmos", workspace, "conversations", project] as const,
  conversations: (workspace: string, project: string, agent?: string) =>
    ["cosmos", workspace, "conversations", project, agent ?? null] as const,
  conversationOf: (workspace: string, project: string) =>
    ["cosmos", workspace, "conversation", project] as const,
  conversation: (workspace: string, project: string, id: string) =>
    ["cosmos", workspace, "conversation", project, id] as const,
};

const scope = (workspace: string, project: string) => ({ workspace, project });

/** The events path of a run, as the platform answers a start with it. */
export const eventsPathOf = (workspace: string, project: string, id: string): string =>
  `/api/v1/workspaces/${encodeURIComponent(workspace)}/projects/${encodeURIComponent(project)}/runs/${encodeURIComponent(id)}/events`;

/** The runs of a project, most recent first (the platform's limit, 200). */
export function useRuns(workspace: string, project: string, filters: RunFilters = {}) {
  const client = useCosmos();
  return useQuery({
    queryKey: runKeys.runs(workspace, project, filters),
    queryFn: () =>
      read(
        z.array(RunView),
        `GET …/projects/${project}/runs`,
        client.GET("/api/v1/workspaces/{workspace}/projects/{project}/runs", {
          params: { path: scope(workspace, project), query: { ...filters, limit: 200 } },
        }),
      ),
    // Every 3 s while a listed run is queued or running, never otherwise.
    refetchInterval: (query) =>
      query.state.data?.some((run) => !FINAL.has(run.status)) ? 3000 : false,
  });
}

/** A run; `poll` reads it again every 2 s until its status is final (its stream has ended). */
export function useRun(workspace: string, project: string, id: string, poll = false) {
  const client = useCosmos();
  return useQuery({
    queryKey: runKeys.run(workspace, project, id),
    queryFn: () =>
      read(
        RunView,
        `GET …/runs/${id}`,
        client.GET("/api/v1/workspaces/{workspace}/projects/{project}/runs/{run_id}", {
          params: { path: { ...scope(workspace, project), run_id: id } },
        }),
      ),
    refetchInterval: (query) =>
      poll && query.state.data && !FINAL.has(query.state.data.status) ? 2000 : false,
  });
}

export function useConversations(workspace: string, project: string, agent?: string) {
  const client = useCosmos();
  return useQuery({
    queryKey: runKeys.conversations(workspace, project, agent),
    queryFn: () =>
      read(
        z.array(ConversationView),
        `GET …/projects/${project}/conversations`,
        client.GET("/api/v1/workspaces/{workspace}/projects/{project}/conversations", {
          params: {
            path: scope(workspace, project),
            query: { ...(agent ? { agent } : {}), limit: 200 },
          },
        }),
      ),
  });
}

/** A conversation with its runs, oldest first; `id` null waits; `poll` reads it every `poll` ms. */
export function useConversation(
  workspace: string,
  project: string,
  id: string | null,
  poll: number | false = false,
) {
  const client = useCosmos();
  return useQuery({
    queryKey: runKeys.conversation(workspace, project, id ?? ""),
    queryFn: () =>
      read(
        ConversationView,
        `GET …/conversations/${id}`,
        client.GET(
          "/api/v1/workspaces/{workspace}/projects/{project}/conversations/{conversation_id}",
          { params: { path: { ...scope(workspace, project), conversation_id: id ?? "" } } },
        ),
      ),
    enabled: id !== null,
    refetchInterval: poll,
  });
}

/**
 * A command on runs; the project's runs and conversations are read again after it.
 * `gone` names the query of what an input erases: it is removed, never read again (it would 404).
 */
function useRunCommand<T, R>(
  workspace: string,
  project: string,
  run: (input: T) => Promise<R>,
  gone?: (input: T) => readonly unknown[],
) {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: async (_data, error, input) => {
      const erased = !error && gone ? gone(input) : null;
      if (erased !== null) {
        await queries.cancelQueries({ queryKey: erased, exact: true });
        queries.removeQueries({ queryKey: erased, exact: true });
      }
      await queries.invalidateQueries({ queryKey: runKeys.runsOf(workspace, project) });
      await queries.invalidateQueries({ queryKey: runKeys.conversationsOf(workspace, project) });
      await queries.invalidateQueries({ queryKey: runKeys.runOf(workspace, project) });
      await queries.invalidateQueries({ queryKey: runKeys.conversationOf(workspace, project) });
    },
  });
}

/** Starts a run of a `rag` agent deployed in `environment`, in a conversation if given. */
export function useStartRun(workspace: string, project: string, agent: string) {
  const client = useCosmos();
  return useRunCommand(
    workspace,
    project,
    (body: { question: string; environment: string; conversation?: string }) =>
      read(
        StartedRun,
        `POST …/agents/${agent}/runs`,
        client.POST("/api/v1/workspaces/{workspace}/projects/{project}/agents/{agent}/runs", {
          params: { path: { ...scope(workspace, project), agent } },
          body: {
            input: { question: body.question },
            environment: body.environment,
            ...(body.conversation ? { conversation: body.conversation } : {}),
          },
        }),
      ),
  );
}

export function useCancelRun(workspace: string, project: string) {
  const client = useCosmos();
  return useRunCommand(workspace, project, (id: string) =>
    read(
      RunView,
      `POST …/runs/${id}/cancel`,
      client.POST("/api/v1/workspaces/{workspace}/projects/{project}/runs/{run_id}/cancel", {
        params: { path: { ...scope(workspace, project), run_id: id } },
        body: {},
      }),
    ),
  );
}

export function useEraseRun(workspace: string, project: string) {
  const client = useCosmos();
  return useRunCommand(
    workspace,
    project,
    async (id: string) => {
      // The client throws a refusal as a ProblemError; anything else that is not a 2xx too.
      const { response } = await client.DELETE(
        "/api/v1/workspaces/{workspace}/projects/{project}/runs/{run_id}",
        { params: { path: { ...scope(workspace, project), run_id: id } } },
      );
      if (!response.ok) throw new Error(`DELETE …/runs/${id} answered ${response.status}`);
    },
    (id) => runKeys.run(workspace, project, id),
  );
}

export function useNewConversation(workspace: string, project: string) {
  const client = useCosmos();
  return useRunCommand(workspace, project, (body: { agent: string; environment: string }) =>
    read(
      CreatedConversation,
      `POST …/projects/${project}/conversations`,
      client.POST("/api/v1/workspaces/{workspace}/projects/{project}/conversations", {
        params: { path: scope(workspace, project) },
        body,
      }),
    ),
  );
}

export function useEraseConversation(workspace: string, project: string) {
  const client = useCosmos();
  return useRunCommand(
    workspace,
    project,
    async (id: string) => {
      // The client throws a refusal as a ProblemError; anything else that is not a 2xx too.
      const { response } = await client.DELETE(
        "/api/v1/workspaces/{workspace}/projects/{project}/conversations/{conversation_id}",
        { params: { path: { ...scope(workspace, project), conversation_id: id } } },
      );
      if (!response.ok) throw new Error(`DELETE …/conversations/${id} answered ${response.status}`);
    },
    (id) => runKeys.conversation(workspace, project, id),
  );
}
